import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import client from "../../api/client";
import { GlobalContext } from "../../context";

function TagsField({ label, values, onChange, placeholder }) {
  const [draft, setDraft] = useState("");

  const addTag = () => {
    const v = draft.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setDraft("");
  };

  const removeTag = (tag) => onChange(values.filter((t) => t !== tag));

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1.5">{label}</label>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {values.map((tag) => (
            <span key={tag} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full">
              {tag}
              <button type="button" onClick={() => removeTag(tag)} className="text-gray-400 hover:text-red-500">×</button>
            </span>
          ))}
        </div>
      )}
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); }
        }}
        onBlur={addTag}
        placeholder={placeholder}
        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30"
      />
    </div>
  );
}

function ScholarshipModal({ scholarship, onClose, onSaved }) {
  const isEdit = !!scholarship?.id;
  const [form, setForm] = useState({
    title: scholarship?.title || "",
    description: scholarship?.description || "",
    deadline: scholarship?.deadline?.slice(0, 10) || "",
    is_active: scholarship?.is_active ?? true,
    application_fee_ngn: scholarship?.application_fee_ngn || "",
    application_fee_usd: scholarship?.application_fee_usd || "",
    scholarship_types: scholarship?.scholarship_types || [],
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await client.put(`/admin/scholarships/${scholarship.id}`, form);
        toast.success("Scholarship updated.");
      } else {
        await client.post("/admin/scholarships", form);
        toast.success("Scholarship created.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-5">{isEdit ? "Edit Scholarship" : "New Scholarship"}</h2>
        <div className="space-y-3">
          <input placeholder="Title" value={form.title}
            onChange={e => setForm(f => ({...f, title: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
          <textarea rows={3} placeholder="Description" value={form.description}
            onChange={e => setForm(f => ({...f, description: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 resize-none" />
          <input type="date" value={form.deadline}
            onChange={e => setForm(f => ({...f, deadline: e.target.value}))}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
          <div className="grid grid-cols-2 gap-3">
            <input type="number" placeholder="Fee (NGN)" value={form.application_fee_ngn}
              onChange={e => setForm(f => ({...f, application_fee_ngn: e.target.value}))}
              className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
            <input type="number" placeholder="Fee (USD)" value={form.application_fee_usd}
              onChange={e => setForm(f => ({...f, application_fee_usd: e.target.value}))}
              className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30" />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.is_active}
              onChange={e => setForm(f => ({...f, is_active: e.target.checked}))} className="rounded" />
            Active
          </label>
          <TagsField
            label="Scholarship Types"
            values={form.scholarship_types}
            onChange={(scholarship_types) => setForm(f => ({...f, scholarship_types}))}
            placeholder="Type a name and press Enter (e.g. Full Scholarship)"
          />
        </div>
        <div className="flex gap-3 mt-6 justify-end">
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving || !form.title}
            className="px-5 py-2.5 rounded-xl bg-[#294a93] text-white text-sm font-semibold disabled:opacity-50 hover:bg-[#1e3a7a] transition-colors">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ScholarshipsPage() {
  const navigate = useNavigate();
  const { authState: { data } } = useContext(GlobalContext);
  const isSuperAdmin = data?.user?.is_superadmin;
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    setLoading(true);
    client.get("/admin/scholarships")
      .then(res => setScholarships(res.data))
      .catch(() => toast.error("Failed to load scholarships."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const confirmDelete = async (id) => {
    if (!window.confirm("Delete this scholarship?")) return;
    setDeleting(id);
    try {
      await client.delete(`/admin/scholarships/${id}`);
      toast.success("Deleted.");
      load();
    } catch { toast.error("Delete failed."); }
    finally { setDeleting(null); }
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Scholarships</h1>
          <p className="text-sm text-gray-500 mt-0.5">{scholarships.length} total — click applications count to view</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => navigate("/dashboard/admin/scholarships/applications")}
            className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">
            View All Applications
          </button>
          {isSuperAdmin && (
            <button onClick={() => setModal({})}
              className="bg-[#294a93] text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-[#1e3a7a] transition-colors">
              + New Scholarship
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
        {loading ? (
          <div className="p-8 space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : scholarships.length === 0 ? (
          <div className="p-16 text-center"><p className="text-4xl mb-3">🎓</p><p className="text-sm font-medium text-gray-600">No scholarships yet</p></div>
        ) : (
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>{["Title", "Fee (NGN)", "Fee (USD)", "Deadline", "Applications", "Status", "Actions"].map(h => (
                <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {scholarships.map(s => (
                <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-800 max-w-[200px]">{s.title}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{s.application_fee_ngn ? `₦${Number(s.application_fee_ngn).toLocaleString()}` : "—"}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs">{s.application_fee_usd ? `$${s.application_fee_usd}` : "—"}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{s.deadline ? new Date(s.deadline).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => navigate(`/dashboard/admin/scholarships/${s.id}/applications`)}
                      className="text-xs font-bold text-[#294a93] hover:underline">
                      {s.applications_count ?? 0} →
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${s.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {s.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {isSuperAdmin ? (
                      <div className="flex gap-2">
                        <button onClick={() => setModal(s)}
                          className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 font-medium text-gray-600">Edit</button>
                        <button onClick={() => confirmDelete(s.id)} disabled={deleting === s.id}
                          className="text-xs px-3 py-1.5 rounded-lg border border-red-100 hover:bg-red-50 font-medium text-red-500 disabled:opacity-50">
                          {deleting === s.id ? "…" : "Delete"}
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal !== null && (
        <ScholarshipModal
          scholarship={modal?.id ? modal : null}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
